import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle, Calendar, FileText, Bank, Download, Plus, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";
import api, { formatApiError, API_BASE } from "../lib/api";
import TeamAvailabilitySidebar from "../components/TeamAvailabilitySidebar";
import LeaveCoachModal from "../components/LeaveCoachModal";

/**
 * HR Self-Service (change request item 11): a slim, employee-facing screen
 * with three tabs — Leave requests, Payslips, Bank/Salary letter. Any user
 * with a linked employee record can use it. If they don't have one yet, we
 * show a nudge to ask HR to link the account.
 */
export default function HrSelfServicePage() {
  const [tab, setTab] = useState("Leaves");
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");

  const loadMe = async () => {
    try {
      const { data } = await api.get("/hr/self/me");
      setMe(data);
    } catch (err) {
      setMe(null);
      setError(formatApiError(err.response?.data?.detail) || "No employee record");
    }
  };

  useEffect(() => { loadMe(); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="hr-self-service-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">HR / SELF-SERVICE</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Your workspace.</h1>
        <p className="text-sm text-zinc-500 mt-2">Request leave, download payslips, and generate bank letters — all in one place.</p>
      </div>

      {me ? (
        <div className="border border-zinc-950 bg-zinc-50 p-4 mb-6" data-testid="my-employee-card">
          <div className="flex items-center gap-4">
            <UserCircle size={40} className="text-zinc-950" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.15em] text-zinc-500">EMPLOYEE {me.employee_no}</div>
              <div className="font-display text-xl font-black tracking-tight">{me.name}</div>
              <div className="text-xs text-zinc-600">{me.position} · {me.department}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6 text-sm text-amber-800" data-testid="no-employee-hint">
          {error || "No employee record linked to your account."} Ask HR to create one and link it to your account so you can request leave and access payslips.
        </div>
      )}

      <div className="border-b border-zinc-200 flex items-center gap-6 mb-6">
        {["Leaves", "Payslips", "Bank Letter"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`self-service-tab-${t.toLowerCase().replace(" ", "-")}`}
            className={`text-xs font-mono tracking-[0.15em] uppercase py-3 border-b-2 -mb-px transition-colors ${
              tab === t ? "border-[#FF3E00] text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Leaves" && <LeavesTab enabled={Boolean(me)} onChange={loadMe} />}
      {tab === "Payslips" && <PayslipsTab enabled={Boolean(me)} />}
      {tab === "Bank Letter" && <BankLetterTab enabled={Boolean(me)} />}
    </div>
  );
}

function LeavesTab({ enabled, onChange }) {
  const [leaves, setLeaves] = useState([]);
  const [form, setForm] = useState({ type: "annual", start_date: "", end_date: "", reason: "", half_day: "" });
  const [busy, setBusy] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/hr/self/leaves");
      setLeaves(data || []);
    } catch (err) {
      // Non-blocking: history load failing shouldn't stop the whole page from rendering.
      // Log for debugging so silent regressions are visible in the console.
      if (process.env.NODE_ENV !== "production") console.error("Failed to load leaves:", err);
    }
  };
  useEffect(() => { if (enabled) load(); }, [enabled]);

  const submit = async (e) => {
    e.preventDefault();
    if (!enabled) return;
    if (form.half_day && form.start_date !== form.end_date) {
      toast.error("Half-day leaves must cover a single date");
      return;
    }
    setBusy(true);
    try {
      await api.post("/hr/self/leaves", { ...form, half_day: form.half_day || null });
      toast.success("Leave request submitted");
      setForm({ type: "annual", start_date: "", end_date: "", reason: "", half_day: "" });
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="text-sm text-zinc-500">
          Not sure when to take leave?{" "}
          <button
            type="button"
            onClick={() => setCoachOpen(true)}
            data-testid="leave-coach-inline-hint"
            className="text-[#FF3E00] font-mono text-xs tracking-wider uppercase hover:underline"
          >
            Ask AI Coach →
          </button>
        </div>
        <button
          type="button"
          onClick={() => setCoachOpen(true)}
          data-testid="leave-coach-open-btn"
          disabled={!enabled}
          className="bg-zinc-950 text-white font-mono text-[10px] tracking-[0.15em] uppercase font-bold px-4 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-2 self-start sm:self-auto"
        >
          <Sparkle size={12} weight="fill" /> AI Leave Coach
        </button>
      </div>

      <form onSubmit={submit} className="border border-zinc-950 mb-6 p-4 grid gap-3 md:grid-cols-[140px_1fr_1fr_100px_2fr_120px]" data-testid="leave-request-form">
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="leave-type-select" className="border-b border-zinc-300 bg-transparent py-1 text-sm">
          {["annual", "sick", "unpaid", "maternity"].map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="leave-start-input" className="border-b border-zinc-300 bg-transparent py-1 text-sm" />
        <input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="leave-end-input" className="border-b border-zinc-300 bg-transparent py-1 text-sm" />
        <select value={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.value })} data-testid="leave-halfday-select" className="border-b border-zinc-300 bg-transparent py-1 text-sm">
          <option value="">Full day</option>
          <option value="am">AM ½</option>
          <option value="pm">PM ½</option>
        </select>
        <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (optional)" data-testid="leave-reason-input" className="border-b border-zinc-300 bg-transparent py-1 text-sm" />
        <button type="submit" disabled={!enabled || busy} data-testid="leave-submit-btn" className="bg-zinc-950 text-white text-xs font-semibold px-4 py-2 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center justify-center gap-1">
          <Plus size={12} /> REQUEST
        </button>
      </form>

      <TeamAvailabilitySidebar start={form.start_date} end={form.end_date} />

      <div className="border border-zinc-200">
        <table className="w-full text-sm">
          <thead className="bg-zinc-950 text-white">
            <tr className="text-xs tracking-wider uppercase">
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">From</th>
              <th className="text-left px-4 py-2">To</th>
              <th className="text-left px-4 py-2">Reason</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Submitted</th>
            </tr>
          </thead>
          <tbody data-testid="my-leaves-tbody">
            {leaves.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-xs font-mono text-zinc-400">NO LEAVE HISTORY</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id} className="border-b border-zinc-100">
                <td className="px-4 py-2 uppercase text-xs font-mono">
                  {l.type}
                  {l.half_day && <span className="ml-1 text-[9px] text-[#FF3E00]">· {l.half_day}</span>}
                </td>
                <td className="px-4 py-2 tnum text-xs">{l.start_date}</td>
                <td className="px-4 py-2 tnum text-xs">{l.end_date}</td>
                <td className="px-4 py-2 text-zinc-600 text-xs">{l.reason || "—"}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 ${
                    l.status === "approved" ? "bg-emerald-50 text-emerald-700"
                    : l.status === "rejected" ? "bg-red-50 text-[#D0021B]"
                    : l.status === "cancelled" ? "bg-zinc-100 text-zinc-500"
                    : "bg-amber-50 text-amber-800"
                  }`}>{(l.status || "pending").toUpperCase()}</span>
                </td>
                <td className="px-4 py-2 text-xs text-zinc-500">{new Date(l.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LeaveCoachModal
        open={coachOpen}
        onClose={() => setCoachOpen(false)}
        onApply={({ start_date, end_date }) => setForm((f) => ({ ...f, start_date, end_date }))}
      />
    </div>
  );
}

function PayslipsTab({ enabled }) {
  const [slips, setSlips] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/hr/self/payslips?months=12").then((r) => setSlips(r.data || [])).catch(() => setSlips([]));
  }, [enabled]);

  const download = (ym) => {
    fetch(`${API_BASE}/api/hr/self/payslips/${ym}/pdf`, { credentials: "include" })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `Payslip-${ym}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="border border-zinc-950" data-testid="payslips-list">
      <table className="w-full text-sm">
        <thead className="bg-zinc-950 text-white">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2">Period</th>
            <th className="text-right px-4 py-2">Gross</th>
            <th className="text-right px-4 py-2">Social Ins.</th>
            <th className="text-right px-4 py-2">Net</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody>
          {slips.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-xs font-mono text-zinc-400">NO PAYSLIPS AVAILABLE</td></tr>}
          {slips.map((s) => (
            <tr key={s.period} className="border-b border-zinc-100" data-testid={`payslip-${s.period}`}>
              <td className="px-4 py-2 font-mono text-xs">{s.period}</td>
              <td className="px-4 py-2 text-right tnum">{s.gross.toLocaleString("en-US", { maximumFractionDigits: 3 })} OMR</td>
              <td className="px-4 py-2 text-right tnum text-zinc-500">({s.social_insurance.toLocaleString("en-US", { maximumFractionDigits: 3 })}) OMR</td>
              <td className="px-4 py-2 text-right tnum font-semibold text-[#FF3E00]">{s.net.toLocaleString("en-US", { maximumFractionDigits: 3 })} OMR</td>
              <td className="px-4 py-2 text-right">
                <button onClick={() => download(s.period)} data-testid={`payslip-download-${s.period}`} className="text-[10px] font-mono text-zinc-700 hover:text-[#FF3E00] flex items-center gap-1 ml-auto">
                  <Download size={10} /> PDF
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BankLetterTab({ enabled }) {
  const [bank, setBank] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!enabled) return;
    setBusy(true);
    try {
      const url = `${API_BASE}/api/hr/self/bank-letter/pdf${bank ? `?bank=${encodeURIComponent(bank)}` : ""}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("failed");
      const b = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = `BankLetter${bank ? `-${bank}` : ""}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Bank letter downloaded");
    } catch { toast.error("Download failed"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-950 p-5 max-w-xl" data-testid="bank-letter-panel">
      <div className="flex items-center gap-3 mb-3">
        <Bank size={22} className="text-[#FF3E00]" />
        <div>
          <div className="font-display text-lg font-black tracking-tight">Salary certificate</div>
          <div className="text-xs text-zinc-500">A formal PDF certifying your employment and current salary — accepted by most banks.</div>
        </div>
      </div>
      <div className="mb-3">
        <label className="label-sm text-zinc-500">Address to (optional)</label>
        <input value={bank} onChange={(e) => setBank(e.target.value)} data-testid="bank-letter-bank-input" placeholder="Bank Muscat" className="w-full border-b border-zinc-300 bg-transparent py-1 text-sm" />
      </div>
      <button onClick={download} disabled={!enabled || busy} data-testid="bank-letter-download-btn" className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2.5 hover:bg-[#FF3E00] disabled:opacity-40 flex items-center gap-1">
        <Download size={12} /> {busy ? "GENERATING…" : "DOWNLOAD PDF"}
      </button>
    </div>
  );
}
